'use client';

import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import { bjToast } from '@components/common';
import { saveResumeInterviewAnswer } from '@services/interview/resumeInterviewApi';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { PromptBarShell } from '@components/features/candidate-prompt-bar/PromptBarShell';
import { ChatBranch } from '@components/features/candidate-prompt-bar/ChatBranch';
import { launchVideo, launchVoice } from './launchers';

interface InterviewAnswerBarProps {
  /** The exact question being answered (stored verbatim on text save). */
  question: string;
  locale: Locale;
  /** Called after the user answered (text saved, or audio/video finished) — parent advances / refreshes. */
  onAnswered?: () => void;
  /** Opening line the voice agent speaks first — voice-websocket needs this to speak at all. */
  firstMessage?: string;
  className?: string;
}

/**
 * Bestie-chat–style answer bar for a single interview question: same building blocks as the
 * /stories answer bar (`StoriesAnswerPromptBar`) — `PromptBarShell` + `ChatBranch` + a matching
 * video-answer button — rendered inline (in the banner questionnaire modal) instead of fixed to
 * the viewport bottom, so both surfaces look and behave identically.
 */
export const InterviewAnswerBar = ({
  question, locale, onAnswered, firstMessage, className,
}: InterviewAnswerBarProps) => {
  const { t } = useTranslation('common');
  const getQuestions = useCallback(() => Promise.resolve([question]), [question]);

  const handleSubmit = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      await saveResumeInterviewAnswer(question, trimmed, locale);
      sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'text', subject: 'profile' });
      onAnswered?.();
    } catch {
      bjToast.error(t('audio-interview.job-landing.error'));
    }
  }, [question, locale, onAnswered, t]);

  const openVoice = useCallback(() => {
    launchVoice({
      getQuestions,
      locale,
      kind: 'answer',
      firstMessage,
      onClosed: () => {
        sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'audio', subject: 'profile' });
        onAnswered?.();
      },
    });
  }, [getQuestions, locale, firstMessage, onAnswered]);

  const openVideo = useCallback(() => {
    launchVideo({
      getQuestions,
      locale,
      onCompleted: () => {
        sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'video', subject: 'profile' });
        onAnswered?.();
      },
    });
  }, [getQuestions, locale, onAnswered]);

  // Video-answer button, styled identically to ChatBranch's mic/send action button (white on
  // secondary) so the two controls read as one unified set — matches StoriesAnswerPromptBar.
  const videoButton = (
    <button
      type="button"
      onClick={openVideo}
      aria-label={t('ai.conversation.video', 'Video')}
      className="flex h-9! w-9! shrink-0 items-center justify-center rounded-lg! bg-secondary p-0! transition-transform hover:scale-110 hover:bg-secondary! lg:h-10! lg:w-10!"
    >
      <VideoCameraIcon className="size-5 text-white" />
    </button>
  );

  return (
    <PromptBarShell inline className={className}>
      <ChatBranch
        onSubmit={handleSubmit}
        onMic={openVoice}
        rightSlot={videoButton}
        placeholder={t('intercv_shout_hint')}
      />
    </PromptBarShell>
  );
};
